import { CookieSetOptions } from 'universal-cookie'

type CookieInformation = {
  key: string
  options: CookieSetOptions
}

// Limit usage of shared domains in order to minimize the internal testing confusion due to bleeding information between environment based subdomains.
const sharedDomain = ['prod', 'preview'].includes(process.env.NEXT_PUBLIC_FIGS_ENV ?? '')
  ? '.wearfigs.com'
  : undefined

const figsCookieKeys = {
  discountCode: {
    key: 'figs-discount-code',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 2419200, // 4 weeks
    },
  },
  authToken: {
    key: 'figs-auth-token',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 15600000, // .5 years
    },
  },
  ssoAuthToken: {
    key: 'figs-sso-access-token',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  authRefreshToken: {
    key: 'figs-refresh-token',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 15600000, // .5 years
    },
  },
  ssoAuthRefreshToken: {
    key: 'figs-sso-refresh-token',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  shopifyCustomerToken: {
    key: 'figs-customer-access-token',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 15600000, // .5 years
    },
  },
  ssoShopifyCustomerToken: {
    key: 'figs-sso-customer-access-token',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  stylePreference: {
    key: 'navGender',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  promoBarDismissed: {
    key: 'figs-promo-bar-dismissed',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  sessionId: {
    key: 'figs-session-id',
    options: {
      domain: sharedDomain, // Set domain to sharedDomain to expose a cookie to checkout's subdomain (example: shop.wearfigs.com)
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 1800, // 30 minutes
    },
  },
  visitorId: {
    key: 'figs-visitor-id',
    options: {
      domain: sharedDomain, // Set domain to sharedDomain to expose a cookie to checkout's subdomain (example: shop.wearfigs.com)
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 34164000, // 13 months (matches Heap duration, also Chrome caps at 400 days)
    },
  },
  contentMode: {
    key: 'figs-content',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 10, // 10 seconds
    },
  },
  productData: {
    key: 'figs-product-data',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 10, // 10 seconds
    },
  },
  initialSessionIndicator: {
    key: 'figs-initial-session',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 3600, // 1 hour
    },
  },
  priorPageViewIndicator: {
    key: 'figs-prior-pageview',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  region: {
    key: 'x-figs-shop-region',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  locale: {
    key: 'x-figs-shop-locale',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  currency: {
    key: 'x-figs-shop-currency',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
  shop: {
    key: 'x-figs-shop-name',
    options: {
      path: '/',
      secure: true,
      httpOnly: false,
      sameSite: 'lax',
      maxAge: 31200000, // 1 year
    },
  },
} as const satisfies Record<PropertyKey, CookieInformation>

const externalCookieKeys = {
  facebook: {
    key: '_fbc',
    options: {},
  },
  oneTrustConsents: {
    key: 'OptanonConsent', // OneTrust cookie that contains info about cookie consents
    options: {},
  },
} as const satisfies Record<PropertyKey, CookieInformation>

export const cookieKeys = {
  ...figsCookieKeys,
  ...externalCookieKeys,
} as const satisfies Record<PropertyKey, CookieInformation>
